import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { motion } from 'framer-motion'
import { useWebSocketData } from '@/hooks/useWebSocketData'
import { useClock } from '@/hooks/useClock'
import { useNotifications } from '@/hooks/useNotifications'
import { FaCheckCircle, FaBell, FaMoneyBillWave, FaBriefcase, FaBolt } from 'react-icons/fa'
import accepted1 from '@/assets/sounds/accepted1.mp3'
import accepted2 from '@/assets/sounds/accepted2.mp3'
import accepted3 from '@/assets/sounds/accepted3.mp3'
import newOpportunitySound from '@/assets/sounds/new_opportunity.mp3'
import projectBilledSound from '@/assets/sounds/project_billed.mp3'
import egeEnergySound from '@/assets/sounds/ege_energy.mp3'

// Design System
import { designTokens } from '@/styles/designTokens'

// Componentes
import OpportunitiesPanel from '@/components/OpportunitiesPanel'
import ContentCarousel from '@/components/ContentCarousel'
import Clock from '@/components/Clock'
import Weather from '@/components/Weather'
import NewsPanel from '@/components/NewsPanel'
import AuthModal from '@/components/AuthModal'
import ConnectionStatus from '@/components/ConnectionStatus'

// Layout Container Principal - Nova Grade Organizada
const AppContainer = styled.div`
  width: 100vw;
  height: 100vh;
  background: ${designTokens.colors.gradients.background};
  font-family: ${designTokens.typography.fontFamily.primary};
  display: grid;
  grid-template-columns: 380px 1fr;
  grid-template-rows: 1fr auto;
  grid-template-areas:
    "sidebar main"
    "sidebar footer";
  gap: ${designTokens.spacing['2xl']};
  padding: ${designTokens.spacing['2xl']};
  overflow: hidden;
`

// Sidebar - Oportunidades (40% mais espaçosa)
const SidebarArea = styled(motion.aside)`
  grid-area: sidebar;
  background: ${designTokens.colors.gradients.card};
  backdrop-filter: blur(10px);
  border-radius: ${designTokens.borderRadius.xl};
  border: 1px solid ${designTokens.colors.neutral.border};
  box-shadow: ${designTokens.shadows.lg};
  overflow: hidden;
  position: relative;
`

// Área Principal - Conteúdo Central (60%)
const MainArea = styled(motion.main)`
  grid-area: main;
  background: ${designTokens.colors.gradients.card};
  backdrop-filter: blur(10px);
  border-radius: ${designTokens.borderRadius.xl};
  border: 1px solid ${designTokens.colors.neutral.border};
  box-shadow: ${designTokens.shadows.lg};
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
`

// Footer - Notícias + Clima + Relógio
const FooterArea = styled.div`
  grid-area: footer;
  height: 160px;
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: ${designTokens.spacing.lg};
`

// Área de Notícias
const NewsArea = styled(motion.div)`
  background: ${designTokens.colors.gradients.card};
  backdrop-filter: blur(10px);
  border-radius: ${designTokens.borderRadius.xl};
  border: 1px solid ${designTokens.colors.neutral.border};
  box-shadow: ${designTokens.shadows.lg};
  overflow: hidden;
  position: relative;
`

// Área de Widgets (Clima + Relógio)
const WidgetsArea = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${designTokens.spacing.lg};
`

const WidgetCard = styled(motion.div)`
  background: ${designTokens.colors.gradients.card};
  backdrop-filter: blur(10px);
  border-radius: ${designTokens.borderRadius.xl};
  border: 1px solid ${designTokens.colors.neutral.border};
  box-shadow: ${designTokens.shadows.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`

// Status de Conexão - Redesignado
const ConnectionStatusStyled = styled.div`
  display: flex;
  align-items: center;
  gap: ${designTokens.spacing.sm};
  font-size: ${designTokens.typography.fontSize.sm};
  font-weight: ${designTokens.typography.fontWeight.medium};
  color: ${props => props.$connected ? designTokens.colors.status.success : designTokens.colors.status.error};
  background: ${designTokens.colors.neutral.backgroundCard};
  border-radius: ${designTokens.borderRadius.full};
  border: 1px solid ${props => props.$connected ? designTokens.colors.status.success : designTokens.colors.status.error};
  backdrop-filter: blur(10px);
  box-shadow: ${designTokens.shadows.sm};
`

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: ${designTokens.borderRadius.full};
  background: ${props => props.$connected ? designTokens.colors.status.success : designTokens.colors.status.error};
  animation: ${props => props.$connected ? 'pulse 2s infinite' : 'none'};
  
  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.7; transform: scale(1.1); }
  }
`

// Shortcuts Info - Redesignado
const ShortcutsInfo = styled.div`
  position: fixed;
  top: ${designTokens.spacing['2xl']};
  right: ${designTokens.spacing['2xl']};
  background: ${designTokens.colors.neutral.backgroundCard};
  border: 1px solid ${designTokens.colors.neutral.borderAccent};
  border-radius: ${designTokens.borderRadius.lg};
  padding: ${designTokens.spacing.lg};
  font-size: ${designTokens.typography.fontSize.sm};
  color: ${designTokens.colors.text.secondary};
  z-index: ${designTokens.zIndex.tooltip};
  backdrop-filter: blur(10px);
  box-shadow: ${designTokens.shadows.xl};
  
  h4 {
    margin: 0 0 ${designTokens.spacing.md} 0;
    color: ${designTokens.colors.primary.blue};
    font-size: ${designTokens.typography.fontSize.lg};
    font-weight: ${designTokens.typography.fontWeight.semibold};
  }
  
  div {
    margin: ${designTokens.spacing.xs} 0;
    line-height: ${designTokens.typography.lineHeight.normal};
  }
`

const ShortcutsToggle = styled.div`
  position: fixed;
  top: ${designTokens.spacing['2xl']};
  right: ${designTokens.spacing['2xl']};
  background: ${designTokens.colors.neutral.backgroundCard};
  border: 1px solid ${designTokens.colors.neutral.border};
  border-radius: ${designTokens.borderRadius.lg};
  padding: ${designTokens.spacing.md} ${designTokens.spacing.lg};
  font-size: ${designTokens.typography.fontSize.sm};
  color: ${designTokens.colors.text.muted};
  z-index: ${designTokens.zIndex.tooltip};
  backdrop-filter: blur(10px);
  cursor: pointer;
  transition: ${designTokens.transitions.normal};
  
  &:hover {
    background: ${designTokens.colors.neutral.backgroundHover};
    color: ${designTokens.colors.text.secondary};
    border-color: ${designTokens.colors.neutral.borderAccent};
  }
`





function App() {
  const { 
    data, 
    connected, 
    error,
    connectionStatus,
    isAuthorized, 
    showAuthModal, 
    handleAuthSuccess 
  } = useWebSocketData()
  const { time: currentTime, date: currentDate } = useClock()
  const { notifications, addNotification, clearAllNotifications } = useNotifications()
  const [showShortcuts, setShowShortcuts] = useState(false)
  const [processedNotifications, setProcessedNotifications] = useState(new Set())
  const [notificationQueue, setNotificationQueue] = useState([])
  const [isPinned, setIsPinned] = useState(false)
  const [opportunityVariation, setOpportunityVariation] = useState(1) // 1, 2, ou 3

  // Adicionar notificações à fila
  useEffect(() => {
    if (notifications.length > 0) {
      const lastNotification = notifications[notifications.length - 1]
      setNotificationQueue(queue => [...queue, lastNotification])
    }
  }, [notifications])

  // Remover notificação da fila após 10s (se não estiver fixada)
  useEffect(() => {
    if (notificationQueue.length === 0 || isPinned) return
    const timer = setTimeout(() => {
      setNotificationQueue(queue => queue.slice(1))
    }, 10000)
    return () => clearTimeout(timer)
  }, [notificationQueue, isPinned])

  // Notificação atual é o topo da fila
  const currentNotification = notificationQueue[0] || null

  // Pegar a notificação mais recente para exibir na moldura da mídia
  // Debug do relógio
  console.log('Clock debug:', { currentTime, currentDate })

  // Processar notificações não exibidas do backend
  useEffect(() => {
    if (data?.notificacoes_nao_exibidas && Array.isArray(data.notificacoes_nao_exibidas)) {
      data.notificacoes_nao_exibidas.forEach(notification => {
        const notificationId = notification.id
        
        // Verificar se já processamos esta notificação
        if (!processedNotifications.has(notificationId)) {
          console.log('Processando notificação não exibida:', notification)
          
          // Mapear o tipo da notificação para o formato esperado
          let eventType = 'opportunity_accepted' // Padrão
          if (notification.data?.tipo === 'Faturamento') {
            eventType = 'project_billed'
          } else if (notification.data?.tipo === 'Pedido') {
            eventType = 'new_opportunity'
          }
          
          // Adicionar a notificação ao sistema local
          addNotification(eventType, {
            ...notification.data,
            bubble_id: notification.id,
            notification_id: notificationId
          })
          
          // Marcar como processada
          setProcessedNotifications(prev => new Set([...prev, notificationId]))
          
          // Enviar requisição para marcar como exibida no backend
          fetch('http://localhost:8000/api/dashboard/mark-notification-displayed/', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              bubble_id: notificationId,
              type: notification.data?.tipo || 'Oportunidade'
            })
          }).catch(error => {
            console.error('Erro ao marcar notificação como exibida:', error)
          })
        }
      })
    }
  }, [data?.notificacoes_nao_exibidas, addNotification, processedNotifications])

  // Função centralizada para disparar notificações
  const triggerNotification = (type, data) => {
    let icon, sound, color, title, message
    switch (type) {
      case 'opportunity_accepted':
      case 'new_opportunity': {
        const ordem = data.ordem || 1
        icon = <FaCheckCircle />
        sound = ordem === 1 ? accepted1 : ordem === 2 ? accepted2 : accepted3
        color = '#FFC107'
        title = 'Oportunidade Aceita!'
        message = `Nova oportunidade aceita: ${data.client}`
        break
      }
      case 'project_billed':
        icon = <FaMoneyBillWave />
        sound = projectBilledSound
        color = '#4CAF50'
        title = 'Projeto Faturado!'
        message = `Projeto faturado: ${data.client}`
        break
      case 'ege_energy_milestone':
        icon = <FaBolt />
        sound = egeEnergySound
        color = '#F57C00'
        title = 'EGE Energy!'
        message = 'Reconhecimentos enviados'
        break
      default:
        icon = <FaBell />
        sound = null
        color = '#888'
        title = 'Notificação'
        message = data.message || ''
    }
    addNotification(type, {
      ...data,
      bubble_id: Date.now(), // Gerar um ID único para a notificação
      notification_id: Date.now() // Gerar um ID único para a notificação
    })
  }

  // Hook para shortcuts de teclado
  useEffect(() => {
    const handleKeyPress = (event) => {
      // Ctrl/Cmd + 1 = Oportunidade Aceita (3 variações)
      if ((event.ctrlKey || event.metaKey) && event.key === '1') {
        event.preventDefault()
        setOpportunityVariation(prev => {
          const next = prev === 3 ? 1 : prev + 1
          triggerNotification('opportunity_accepted', {
          id: Date.now(),
          client: 'Cliente Teste (Shortcut)',
          value: '75000',
            description: 'Projeto via shortcut',
            ordem: next, // Usar para escolher ícone/som
            variation: next // 1=sino, 2=gongo, 3=haka
        })
          console.log(`Shortcut: Oportunidade Aceita (Variação ${next})`)
          return next
        })
      }
      
      // Ctrl/Cmd + 2 = Nova Oportunidade
      if ((event.ctrlKey || event.metaKey) && event.key === '2') {
        event.preventDefault()
        triggerNotification('new_opportunity', {
          id: Date.now(),
          client: 'Cliente Teste (Shortcut)',
          description: 'Nova oportunidade via shortcut'
        })
        console.log('Shortcut: Nova Oportunidade disparada')
      }
      
      // Ctrl/Cmd + 3 = Projeto Faturado
      if ((event.ctrlKey || event.metaKey) && event.key === '3') {
        event.preventDefault()
        triggerNotification('project_billed', {
          id: Date.now(),
          client: 'Cliente Teste (Shortcut)',
          value: '120000',
          description: 'Projeto faturado via shortcut'
        })
        console.log('Shortcut: Projeto Faturado disparado')
      }
      
      // Ctrl/Cmd + 4 = EGE Energy
      if ((event.ctrlKey || event.metaKey) && event.key === '4') {
        event.preventDefault()
        triggerNotification('ege_energy_milestone', {
          id: Date.now(),
          total_reconhecimentos: 20
        })
        console.log('Shortcut: EGE Energy disparado')
      }
      
      // Ctrl/Cmd + 0 = Limpar todas as notificações
      if ((event.ctrlKey || event.metaKey) && event.key === '0') {
        event.preventDefault()
        clearAllNotifications()
        console.log('Shortcut: Limpar notificações')
      }
      
      // Ctrl/Cmd + H = Toggle shortcuts
      if ((event.ctrlKey || event.metaKey) && event.key === 'h') {
        event.preventDefault()
        setShowShortcuts(prev => !prev)
        console.log('Shortcut: Toggle shortcuts -', !showShortcuts)
      }

      // Ctrl/Cmd + P = Fixar/desfixar notificação
      if ((event.ctrlKey || event.metaKey) && (event.key === 'p' || event.key === 'P')) {
        event.preventDefault()
        setIsPinned(prev => !prev)
        console.log('Shortcut: Toggle pin notification -', !isPinned)
      }
    }

    document.addEventListener('keydown', handleKeyPress)
    
    return () => {
      document.removeEventListener('keydown', handleKeyPress)
    }
  }, [addNotification, isPinned])

  // Efeito para simular notificações (remover em produção)

  return (
    <AppContainer>
      {/* Status de Conexão WebSocket */}
      {(!connected || connectionStatus === 'reconnecting') && (
        <ConnectionStatus 
          connectionStatus={connectionStatus}
          connected={connected}
          error={error}
        />
      )}

      {/* Sidebar - Painel de Oportunidades */}
      <SidebarArea
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        <OpportunitiesPanel opportunities={data?.oportunidades || []} />
      </SidebarArea>

      {/* Área Principal - Carrossel de Conteúdo */}
      <MainArea
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <ContentCarousel 
          media={data?.media || []} 
          notification={currentNotification} 
          isPinned={isPinned} 
        />
      </MainArea>

      {/* Footer - Notícias + Widgets */}
      <FooterArea>
        {/* Área de Notícias */}
        <NewsArea
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <NewsPanel news={data?.noticias || []} />
        </NewsArea>

        {/* Widgets - Clima e Relógio */}
        <WidgetsArea>
          <WidgetCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Weather data={data?.clima} compact={true} />
          </WidgetCard>

          <WidgetCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <Clock 
              time={currentTime || '00:00:00'} 
              date={currentDate || 'Carregando...'} 
              compact={true} 
            />
          </WidgetCard>
        </WidgetsArea>
      </FooterArea>
      
      {/* Informações de Shortcuts (apenas em desenvolvimento) */}
      {import.meta.env.DEV && showShortcuts && (
        <ShortcutsInfo>
          <h4>🎯 Shortcuts</h4>
          <div>Ctrl+1: Oportunidade Aceita (Variação {opportunityVariation})</div>
          <div>Ctrl+2: Nova Oportunidade</div>
          <div>Ctrl+3: Projeto Faturado</div>
          <div>Ctrl+4: EGE Energy</div>
          <div>Ctrl+0: Limpar Notificações</div>
          <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.2)', color: '#888' }}>
            Ctrl+H: Ocultar/Mostrar
          </div>
        </ShortcutsInfo>
      )}
      
      {/* Modal de Autenticação */}
      <AuthModal
        isVisible={showAuthModal}
        onAuthSuccess={handleAuthSuccess}
        onClose={() => setShowAuthModal(false)}
      />
      
    </AppContainer>
  )
}

export default App
